import { Injectable } from '@angular/core';
import { UserProfileModel } from 'src/app/core/models/user-profile.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { AffectationModel } from '../../../../core/models/affectation.model';
import { map } from 'rxjs/operators';
import { EmployeePositionModel } from '../../../../core/models/employee-position.model';
import {
  UserProfileValidatorLevel,
  UserProfileValidatorSeuil,
} from '../../../../core/enums/user-profile-validator-seuil.enum';
import { CategoryModel } from '../../../../core/models/category.model';
import { RoleModel, UserProfileRoleModel } from '../../../../core/models/role.model';
import { PrestationModel } from '../../../../core/models/prestation.model';
import { ContactTypeModel } from '../../../../core/models/contact-type.model';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService extends BaseService<UserProfileModel> {
  defaultSort = 'createdAt';

  protected _validators = new BehaviorSubject<CategoryModel[]>([] as CategoryModel[]);
  readonly validators$ = this._validators.asObservable();

  constructor(
    public http: HttpClient,
    public authService: AuthService,
  ) {
    super(http, 'userProfile', authService);
  }

  deletingValidatorsTechnicalCategories() {
    return this.http.delete<ObjectApiInterface<UserProfileModel>>(
      `${this.baseUrlApi}/deleteEmptyValidatorsTechnicalCategories`,
    );
  }

  findUsedContactTypes(): Observable<ObjectApiInterface<ContactTypeModel[]>> {
    return this.http.get<ObjectApiInterface<ContactTypeModel[]>>(`${this.baseUrlApi}/contact-type/list`);
  }

  findUserAffectation() {
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/user/affectation/list`);
  }

  getResume(id: string): Observable<ObjectApiInterface<UserProfileModel>> {
    return this.http.get<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/resume/${id}`);
  }

  getEmployeePositionRelation(_id): Observable<ObjectApiInterface<UserProfileModel[]>> {
    return this.http.get<ObjectApiInterface<UserProfileModel[]>>(
      `${this.baseUrlApi}/getEmployeePositionRelation/${_id}`,
    );
  }

  getContactTypeRelation(_id): Observable<ObjectApiInterface<UserProfileModel[]>> {
    return this.http.get<ObjectApiInterface<UserProfileModel[]>>(`${this.baseUrlApi}/getContactTypeRelation/${_id}`);
  }

  getGedCredential() {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/get/gedCredential`);
  }

  addAffectation(id: string, affectation: AffectationModel) {
    // remove useless data in affectations.establishment
    if (affectation.establishment) {
      affectation.establishment = {
        _id: affectation.establishment._id,
        label: affectation.establishment.label,
        technicalIdentifier: affectation.establishment.technicalIdentifier,
      };
    }

    if (affectation.responsable) {
      affectation.responsable = {
        _id: affectation.responsable._id,
        firstName: affectation.responsable.firstName,
        lastName: affectation.responsable.lastName,
        fullName: affectation.responsable.fullName,
        mcEmail: affectation.responsable.mcEmail,
      };
    }

    if (affectation.contactTypes) {
      affectation.contactTypes = affectation.contactTypes.map(ct => ({
        _id: ct._id,
        label: ct.label,
        typology: ct.typology,
        technicalIdentifier: ct.technicalIdentifier,
      }));
    }

    if (affectation.activities) {
      affectation.activities = affectation.activities.map(a => ({ _id: a._id, label: a.label }));
    }

    return this.http
      .post<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/${id}/addAffectation`, affectation)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x.data;
        }),
      );
  }

  updateAffectation(id: string, affectation: AffectationModel) {
    if (affectation.activities) {
      affectation.activities = affectation.activities.map(a => ({ _id: a._id, label: a.label }));
    }

    return this.http
      .put<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/${id}/updateAffectation`, affectation)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteAffectation(id: string, affectationId: string) {
    return this.http
      .delete<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/${id}/deleteAffectation/${affectationId}`)
      .pipe(
        map((x: ObjectApiInterface<UserProfileModel>) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteAffectationByEstablishmentId(id: string, establishmentId: string) {
    return this.http
      .delete<
        ObjectApiInterface<UserProfileModel>
      >(`${this.baseUrlApi}/${id}/deleteAffectationByEstablishment/${establishmentId}`)
      .pipe(
        map((x: ObjectApiInterface<UserProfileModel>) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  addEmployeePosition(id: string, employeePosition: EmployeePositionModel) {
    return this.http
      .post<ObjectApiInterface<EmployeePositionModel>>(`${this.baseUrlApi}/${id}/addEmployeePosition`, employeePosition)
      .pipe(
        map(x => {
          this._entity.value.employeePositions.push(x.data);
          return x;
        }),
      );
  }

  updateEmployeePosition(id: string, _oldId: string, employeePosition: EmployeePositionModel) {
    return this.http
      .put<
        ObjectApiInterface<EmployeePositionModel>
      >(`${this.baseUrlApi}/${id}/updateEmployeePosition/${_oldId}`, employeePosition)
      .pipe(
        map(x => {
          this._entity.value.employeePositions = this._entity.value.employeePositions.filter(y => y._id !== _oldId);
          this._entity.value.employeePositions.push(employeePosition);
          return x;
        }),
      );
  }

  deleteEmployeePosition(id: string, employeePositionId: string) {
    return this.http
      .delete<
        ObjectApiInterface<EmployeePositionModel>
      >(`${this.baseUrlApi}/${id}/deleteEmployeePosition/${employeePositionId}`)
      .pipe(
        map(x => {
          this._entity.value.employeePositions = this._entity.value.employeePositions.filter(y => y._id !== x.data._id);
          return x;
        }),
      );
  }

  getValidator(id: string, matriceLevel: UserProfileValidatorLevel) {
    return this.http
      .get<ObjectApiInterface<CategoryModel[]>>(`${this.baseUrlApi}/${id}/getValidator?matriceLevel=${matriceLevel}`)
      .pipe(
        map(x => {
          this._validators.next(x.data);
          return x;
        }),
      );
  }

  setValidator(
    id: string,
    categoryId: string,
    seuil: UserProfileValidatorSeuil,
    matriceLevel: UserProfileValidatorLevel,
  ) {
    return this.http
      .post<ObjectApiInterface<CategoryModel>>(`${this.baseUrlApi}/${id}/setValidator`, {
        seuil,
        category: categoryId,
        matriceLevel,
      })
      .pipe(
        map(x => {
          const exist = this._validators.value.find(y => y._id === x.data.category);

          if (exist) {
            this._validators.value.map(y => {
              if (y._id === x.data.category) {
                y.seuil = x.data.seuil;

                // map same seuil to all category.children
                y.children.map(z => {
                  z.seuil = x.data.seuil;
                });
              }
              return y;
            });
          } else {
            this._validators.value.push(x.data);
          }
          return x;
        }),
      );
  }

  setAllValidator(id: string, seuil: UserProfileValidatorSeuil, matriceLevel: UserProfileValidatorLevel) {
    return this.http
      .post<
        ObjectApiInterface<CategoryModel[]>
      >(`${this.baseUrlApi}/${id}/setAllValidator/${seuil}/${matriceLevel}`, {})
      .pipe(
        map(x => {
          this._validators.value.map(y => {
            y.seuil = seuil;

            // map same seuil to all category.children
            y.children.map(z => {
              z.seuil = seuil;
            });

            return y;
          });
          // this._validators.next(x.data);
          return x;
        }),
      );
  }

  setCredentialGed(id, credential) {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/setCredentialGed/${id}`, credential).pipe(
      map(x => {
        this._entity.value.gedCredential = credential;
        return x;
      }),
    );
  }

  updateUserProfileRoles(id: string, data: Array<RoleModel>): Observable<any> {
    return this.http
      .put<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/${id}/update-user-profile-roles`, { roles: data })
      .pipe(
        map((x: any) => {
          // this._entity.value.employeePositions = this._entity.value.employeePositions.ticket-filter(y => y._id !== _oldId);
          // this._entity.value.employeePositions.push(employeePosition);
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  assignCategories(id: string, data: CategoryModel, toogleValue: boolean): Observable<any> {
    return this.http
      .post<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/${id}/assign-categories/${toogleValue}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);

          return x;
        }),
      );
  }

  exportUserProfiles(): Observable<ObjectApiInterface<any>> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/export-user-profiles`);
  }

  downloadUsersTestTemplate(): Observable<ObjectApiInterface<UserProfileModel>> {
    return this.http.get<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/downloadUsersTestTemplate/`);
  }

  importUsersTest(data: any): Observable<ObjectApiInterface<UserProfileModel>> {
    return this.http.post<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/importUsersTest`, data, {
      headers: { 'Error-CRUD': 'IMPORT' },
    });
  }

  updateFromProfil(entity: UserProfileModel): Observable<ObjectApiInterface<UserProfileModel>> {
    return this.http
      .put<ObjectApiInterface<UserProfileModel>>(`${this.baseUrlApi}/updateFromProfil/${entity['_id']}`, entity, {
        headers: { 'Error-CRUD': 'UPDATE' },
      })
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }
}
